<script setup lang="ts">
import type { SliceTypeFragment } from '#graphql-operations'

type SliceData = NonNullable<SliceTypeFragment['Slices'][number]>

defineProps({
  slices: {
    type: Array,
    required: true,
  },
  sliceOffset: {
    type: Number,
    default: 0,
  },
})

const getSliceComponent = (slice: SliceData) => {
  switch (slice?.__typename?.replace('ComponentSlices', '')) {
    case 'Advice':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Advice.vue`))
    case 'BannerFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/BannerFeed.vue`))
    case 'BannerWithMedia':
      return defineAsyncComponent(() => import(`~layers/components/Slices/BannerWithMedia.vue`))
    case 'BannerWithShopTheLook':
      return defineAsyncComponent(() => import(`~layers/components/Slices/BannerWithShopTheLook.vue`))
    case 'Buttons':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Buttons.vue`))
    case 'Categories':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Categories.vue`))
    case 'CustomerServicePages':
      return defineAsyncComponent(() => import(`~layers/components/Slices/CustomerServicePages.vue`))
    case 'Embed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Embed.vue`))
    case 'Faq':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Faq.vue`))
    case 'Icons':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Icons.vue`))
    // case 'Marquee':
    //   return defineAsyncComponent(() => import(`~layers/components/Slices/Marquee.vue`))
    case 'MediaFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/MediaFeed.vue`))
    case 'MediaWithShopTheLook':
      return defineAsyncComponent(() => import(`~layers/components/Slices/MediaWithShopTheLook.vue`))
    case 'PageFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/PageFeed.vue`))
    case 'ReviewRelationFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/ReviewRelationFeed.vue`))
    case 'ProductsCollectionFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/ProductsCollectionFeed.vue`))
    case 'ProductsFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/ProductsFeed.vue`))
    case 'QuoteWithShopTheLook':
      return defineAsyncComponent(() => import(`~layers/components/Slices/QuoteWithShopTheLook.vue`))
    case 'RichtextFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/RichtextFeed.vue`))
    case 'RichtextWithMedia':
    case 'RichtextWithImage':
      return defineAsyncComponent(() => import(`~layers/components/Slices/RichtextWithMedia.vue`))
    case 'RichtextWithProduct':
      return defineAsyncComponent(() => import(`~layers/components/Slices/RichtextWithProduct.vue`))
    case 'Spacer':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Spacer.vue`))
    case 'SpotlerProductSlider':
      return defineAsyncComponent(() => import(`~layers/components/Slices/SpotlerProductSlider.vue`))
    case 'ShopTheLook':
      return defineAsyncComponent(() => import(`~layers/components/Slices/ShopTheLookFeed.vue`))
    case 'StoreSelector':
      return defineAsyncComponent(() => import(`~layers/components/Slices/StoreSelector.vue`))
    case 'Table':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Table.vue`))
    case 'Usp':
      return defineAsyncComponent(() => import(`~layers/components/Slices/Usp.vue`))
    case 'VideoEmbedFeed':
      return defineAsyncComponent(() => import(`~layers/components/Slices/VideoEmbedFeed.vue`))
    default:
      return defineAsyncComponent(() => import(`~layers/components/Slices/Unknown.vue`))
  }
}
</script>

<template>
  <div
    v-memo="slices"
    class="content-types"
  >
    <template
      v-for="(slice, index) in slices"
      :key="index"
    >
      <slot
        :name="`before-slice-${index}`"
        :slice="slice"
        :index="index + sliceOffset"
      />

      <component
        :is="getSliceComponent(slice)"
        :slice="slice"
        :index="index + sliceOffset"
        :slices="slices"
      />

      <slot
        :name="`after-slice-${index}`"
        :slice="slice"
        :index="index + sliceOffset"
      />
    </template>
  </div>
</template>

<style>
@import url("~layers/components/Strapi/Slices.css");
</style>
